/* eslint-disable react-native/no-inline-styles */
import React, { useCallback, useEffect, useState } from 'react';
import { View, StyleSheet } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { format, formatISO, startOfDay, endOfDay, endOfMonth } from 'date-fns';

import * as Layout from '@components/screen/Layout';
import ScrollContainer from '@components/global/ScrollContainer';
import Divider from '@components/global/Divider';
import useApi from '@/hooks/useApi';
import { MsB2b } from '@api/ms-b2b';
import SalesmanFilterBar from './components/SalesmanFilterBar';
import SalesmanVisitTrendChart from './components/SalesmanVisitTrendChart';
import SalesmanDurationChart from './components/SalesmanDurationChart';
import SalesmanBusinessTypeChart from './components/SalesmanBusinessTypeChart';
import SalesmanVisitStatusChart from './components/SalesmanVisitStatusChart';
import SalesmanArTable, { AR_PAGE_SIZE } from './components/SalesmanArTable';
import SalesmanPerformingTable, {
  PERFORMING_PAGE_SIZE,
} from './components/SalesmanPerformingTable';
import * as Resources from '@/store/resources/actions';
import SalesmanMetricCard, {
  CAPTION_FONT_SIZE,
  formatCount,
} from './components/SalesmanMetricCard';
import SociollaText from '@components/sociolla/SociollaText';
import Colors from '@global/colors';
import { isWeb, isMobileWeb } from '@global/screens';

// The whole current month, first to last day, not month-to-date.
const currentMonthRange = () => {
  const now = new Date();
  return {
    from: formatISO(startOfDay(new Date(now.getFullYear(), now.getMonth(), 1))),
    to: formatISO(endOfDay(endOfMonth(now))),
  };
};

// DOORS and Master Customer ignore the Period filter, so their caption has to name the month.
const currentMonthLabel = () =>
  `${format(new Date(), 'MMMM yyyy')} (current month)`;

// %AR Target vs Outstanding is the only figure on the page that carries a colour, and only to
// warn: above its market type's threshold too much of what is still owed is already past grace.
// GT is held to a tighter share than MT. Every other number is plain.
const OUTSTANDING_MAX_PCT = { gt: 5, mt: 10 };

// AR figures are reported per market type, never as a total (Decision 65). Fixed rather than
// read from the response: the backend always returns both.
const AR_BUCKETS = [
  { key: 'gt', label: 'GT' },
  { key: 'mt', label: 'MT' },
];

// B2B-5920. The last card's pagination sits at the very end of the scroll, and padding on the
// scroll container's own frame reserves nothing after the last child: on native it only shrinks
// the viewport, and on mobile web the fixed-height box counts it inside the height. So the
// clearance rides on the scrolled content instead. Desktop web is left alone — the container does
// not scroll there, so its frame padding is ordinary page padding and still renders. The Android
// nav bar draws over the app at targetSdk 36 and mobile web has its own browser chrome, which is
// why both add the safe-area inset, the same allowance `Layout`'s sticky button bar makes.
const scrollClearance = (insetBottom) =>
  isWeb
    ? undefined
    : { paddingBottom: 30 + insetBottom + (isMobileWeb ? 12 : 0) };

const currentYearMonth = () => {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
};

// axios serialises nested objects into the bracket notation the backend parser expects, with
// explicit numeric indices: filter[user.id][$in][0]=x&...[1]=y. The empty-bracket form would
// collapse $in to a string and be silently ignored.
const withScope = (filter, { salesTeamId, salesmanIds }) => {
  const scoped = { ...filter };
  if (salesTeamId) {
    scoped['sales_team.id'] = salesTeamId;
  }
  if (salesmanIds?.length) {
    scoped['user.id'] = { $in: salesmanIds };
  }
  return scoped;
};

const SalesmanDashboardScreen = () => {
  const dispatch = useDispatch();
  const insets = useSafeAreaInsets();
  // Business type names for the donut's drill-down: the API returns codes only.
  const marketTypes = useSelector((state) => state.resources.marketTypes) || [];

  // Applied, not draft: the filter bar holds its own draft and only pushes here on Apply.
  const [applied, setApplied] = useState(() => ({
    ...currentMonthRange(),
    salesTeamId: undefined,
    salesmanIds: [],
  }));
  // Visit Trend, Performing Salesman and the AR table each own a month; none follows Period.
  const [trendPeriod, setTrendPeriod] = useState(currentYearMonth);
  const [performingPeriod, setPerformingPeriod] = useState(currentYearMonth);
  const [performingSkip, setPerformingSkip] = useState(0);
  const [arPeriod, setArPeriod] = useState(currentYearMonth);
  const [arSkip, setArSkip] = useState(0);

  const summaryApi = useApi(MsB2b.getSalesmanDashboardSummary);
  const receivablesApi = useApi(MsB2b.getSalesmanDashboardReceivables);
  const arTableApi = useApi(MsB2b.getSalesmanDashboardArBySalesman);
  const visitTrendApi = useApi(MsB2b.getSalesmanDashboardVisitTrend);
  const performingApi = useApi(MsB2b.getSalesmanDashboardPerformingSalesman);

  const fetchAll = useCallback(
    (scope) => {
      const rangeFilter = withScope(
        { visited_at: { $gte: scope.from, $lte: scope.to } },
        scope,
      );

      summaryApi.request({ params: { filter: rangeFilter } });
      // Cards only, and always the current month, so they take no period of their own.
      receivablesApi.request({ params: { filter: withScope({}, scope) } });
      arTableApi.request({
        params: {
          filter: withScope({ ...arPeriod }, scope),
          skip: 0,
          limit: AR_PAGE_SIZE,
        },
      });
      setArSkip(0);
      visitTrendApi.request({
        params: { filter: withScope({ ...trendPeriod }, scope) },
      });
      performingApi.request({
        params: {
          filter: withScope({ ...performingPeriod }, scope),
          skip: 0,
          limit: PERFORMING_PAGE_SIZE,
        },
      });
      setPerformingSkip(0);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trendPeriod, performingPeriod, arPeriod],
  );

  // No fetch on mount. The Sales Team filter has no "All" any more, so every read is scoped to
  // one team, and the filter bar calls onApply as soon as it knows which — fetching here too
  // would load the whole scope first and then reload, five wasted requests and a visible jump.
  useEffect(() => {
    dispatch(Resources.getMarketTypes({ isAll: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onApply = (scope) => {
    setApplied(scope);
    fetchAll(scope);
  };

  const onChangeTrendPeriod = (next) => {
    setTrendPeriod(next);
    visitTrendApi.request({
      params: { filter: withScope({ ...next }, applied) },
    });
  };

  // Pages on the backend, so moving pages is a request rather than a slice of a set in hand.
  const requestPerforming = (next, skip, scope) => {
    setPerformingSkip(skip);
    performingApi.request({
      params: {
        filter: withScope({ ...next }, scope),
        skip,
        limit: PERFORMING_PAGE_SIZE,
      },
    });
  };

  const onChangePerformingPeriod = (next) => {
    setPerformingPeriod(next);
    requestPerforming(next, 0, applied);
  };

  const onChangePerformingPage = (skip) => {
    requestPerforming(performingPeriod, skip, applied);
  };

  // Its own endpoint, so a month or page change re-reads the table alone; the cards above are
  // always the current month and are not touched.
  const requestArTable = (next, skip, scope) => {
    setArSkip(skip);
    arTableApi.request({
      params: {
        filter: withScope({ ...next }, scope),
        skip,
        limit: AR_PAGE_SIZE,
      },
    });
  };

  const onChangeArPeriod = (next) => {
    setArPeriod(next);
    requestArTable(next, 0, applied);
  };

  const onChangeArPage = (skip) => {
    requestArTable(arPeriod, skip, applied);
  };

  // Locks the filter bar while any read is in flight: useApi resets state on every response,
  // which re-renders under an open date picker and snaps its calendar back.
  const isFetching =
    summaryApi.loading ||
    receivablesApi.loading ||
    arTableApi.loading ||
    visitTrendApi.loading ||
    performingApi.loading;

  const summary = summaryApi.data || {};
  const receivables = receivablesApi.data || {};
  // useApi seeds data with [] before the first response, hence the guard on the shape.
  const trendBuckets = visitTrendApi.data?.buckets || [];

  // Every AR card reads its own bucket; the totals the response carries are not shown anywhere.
  const arBucket = (bucket) => receivables.by_market_type?.[bucket] || {};

  // Current month like DOORS, so the caption names it. On a Cerebro outage the backend ships
  // the collection with a null target, so only the target line degrades.
  const arCaption = (bucket) => {
    const ar = arBucket(bucket);
    const month = `${currentMonthLabel()}\n`;
    if (receivables.unavailable || ar.ar_target == null) {
      return `${month}target unavailable`;
    }
    // Nothing past grace for this bucket: zero over zero is not "behind target".
    if (!ar.ar_target) {
      return `${month}no target this month`;
    }
    return (
      <>
        {`${month}${ar.pct_ar_vs_target || 0}% of `}
        <SociollaText currency style={styles.captionFigure}>
          {ar.ar_target}
        </SociollaText>
        {' target'}
      </>
    );
  };

  // The share of what is still owed that is already past grace. The threshold runs the other
  // way to %AR vs Target: here a high share is the bad one.
  const outstandingCaption = (bucket) => {
    const ar = arBucket(bucket);
    const month = currentMonthLabel();
    if (receivables.unavailable || !ar.outstanding_ar) {
      return month;
    }
    const pct = ar.pct_target_vs_outstanding || 0;
    const over = pct > OUTSTANDING_MAX_PCT[bucket];
    return (
      <>
        {`${month}\n`}
        {over ? (
          <SociollaText style={styles.behind}>{`${pct}%`}</SociollaText>
        ) : (
          `${pct}%`
        )}
        {' of total AR remains outstanding'}
      </>
    );
  };

  return (
    <Layout.Container
      style={isWeb ? styles.containerWeb : styles.containerMobile}>
      <ScrollContainer
        style={styles.containerScroll}
        contentContainerStyle={scrollClearance(insets.bottom)}
        showsVerticalScrollIndicator>
        {/* Mobile already shows the screen name in the navigation header. */}
        {isWeb ? <Layout.Title title="Sales Activity Salesman" /> : null}

        <SalesmanFilterBar
          value={applied}
          loading={isFetching}
          onApply={onApply}
        />

        {/* On mobile the filter is its own card on grey, so the rule is redundant. */}
        {isWeb ? (
          <View style={styles.dividerWrap}>
            <Divider height={2} color={Colors.grey0} fullBleed={false} />
          </View>
        ) : null}

        {/* One indicator for all four reads; the figures on screen stay readable meanwhile. */}
        <View style={styles.status}>
          {isFetching ? (
            <>
              <View style={styles.statusSpinner}>
                <Layout.Loader type="tiny" />
              </View>
              <SociollaText style={styles.statusText}>Updating</SociollaText>
            </>
          ) : null}
        </View>

        <View style={styles.cards}>
          {/* One View per mockup row: the 3 / 2 / 4 grouping is editorial, so a single
              wrapping row would pack every card onto one line on a wide screen. */}
          {/* "Avg. Visit Duration" wraps on a phone; the other two reserve the same lines. */}
          <View style={styles.cardRow}>
            <SalesmanMetricCard
              label="Total Visit"
              labelLines={2}
              value={summary.total_visit}
              caption={`${formatCount(summary.total_visit)} / ${formatCount(
                summary.plan_activity_total,
              )} planned (${summary.pct_finished_plan || 0}%)`}
              loading={summaryApi.loading}
              error={summaryApi.error}
            />

            <SalesmanMetricCard
              label="Effective Call"
              labelLines={2}
              value={summary.effective_call}
              caption="new orders this period"
              loading={summaryApi.loading}
              error={summaryApi.error}
            />

            <SalesmanMetricCard
              label="Avg. Visit Duration"
              labelLines={2}
              value={summary.avg_visit_duration_minutes}
              unit="min"
              caption="finished visits only"
              loading={summaryApi.loading}
              error={summaryApi.error}
            />
          </View>

          <View style={styles.cardRow}>
            <SalesmanMetricCard
              label="DOORS"
              value={summary.doors}
              caption={currentMonthLabel()}
              loading={summaryApi.loading}
              error={summaryApi.error}
            />

            <SalesmanMetricCard
              label="Master Customer"
              value={summary.master_customer}
              caption={`active & approved customers, as of ${currentMonthLabel()}`}
              loading={summaryApi.loading}
              error={summaryApi.error}
            />
          </View>

          {/* All four AR figures on one line: Collection beside Outstanding for each market type
              (Decision 65). The totals are not shown. `wideCard` is what breaks the line, so a
              phone still stacks them one per row. Cerebro only: an outage sets `unavailable`,
              else a null would print as Rp 0. */}
          <View style={styles.cardRow}>
            {AR_BUCKETS.map(({ key, label }) => (
              <SalesmanMetricCard
                key={`collection-${key}`}
                label={`AR Collection ${label}`}
                value={arBucket(key).ar_collection}
                currency
                caption={arCaption(key)}
                loading={receivablesApi.loading}
                error={receivablesApi.error}
                style={styles.wideCard}
              />
            ))}
            {AR_BUCKETS.map(({ key, label }) => (
              <SalesmanMetricCard
                key={`outstanding-${key}`}
                label={`Outstanding AR ${label}`}
                value={arBucket(key).outstanding_ar}
                currency
                caption={outstandingCaption(key)}
                loading={receivablesApi.loading}
                error={receivablesApi.error || Boolean(receivables.unavailable)}
                style={styles.wideCard}
              />
            ))}
          </View>

          <View style={styles.cardRow}>
            <SalesmanVisitTrendChart
              buckets={trendBuckets}
              period={trendPeriod}
              loading={visitTrendApi.loading}
              error={visitTrendApi.error}
              onChangePeriod={onChangeTrendPeriod}
              style={styles.chartCard}
            />

            <SalesmanDurationChart
              buckets={summary.duration_distribution || []}
              loading={summaryApi.loading}
              error={summaryApi.error}
              style={styles.chartCard}
            />
          </View>

          <View style={styles.cardRow}>
            <SalesmanVisitStatusChart
              status={summary.visit_status}
              loading={summaryApi.loading}
              error={summaryApi.error}
              style={styles.chartCard}
            />

            <SalesmanBusinessTypeChart
              rows={summary.visit_by_business_type || []}
              marketTypes={marketTypes}
              loading={summaryApi.loading}
              error={summaryApi.error}
              style={styles.chartCard}
            />
          </View>

          <SalesmanArTable
            rows={arTableApi.data?.rows || []}
            totalData={arTableApi.data?.total_data || 0}
            skip={arSkip}
            period={arPeriod}
            loading={arTableApi.loading}
            error={arTableApi.error}
            onChangePeriod={onChangeArPeriod}
            onChangePage={onChangeArPage}
          />

          <SalesmanPerformingTable
            rows={performingApi.data?.rows || []}
            totalData={performingApi.data?.total_data || 0}
            skip={performingSkip}
            onChangePage={onChangePerformingPage}
            period={performingPeriod}
            loading={performingApi.loading}
            error={performingApi.error}
            onChangePeriod={onChangePerformingPeriod}
          />
        </View>
      </ScrollContainer>
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  containerWeb: { paddingBottom: 0, paddingHorizontal: 0, paddingTop: 0 },
  containerMobile: {
    backgroundColor: Colors.grey5,
    paddingBottom: 10,
    paddingHorizontal: 0,
    paddingTop: 0,
  },
  containerScroll: {
    // Desktop web only; every other platform gets it via `scrollClearance` above.
    paddingBottom: isWeb ? 30 : 0,
    paddingHorizontal: isWeb ? 30 : 0,
    paddingTop: isWeb ? 30 : 0,
  },
  dividerWrap: { marginBottom: 0, marginTop: 16 },
  cards: {
    gap: isWeb ? 12 : 8,
    paddingHorizontal: isWeb ? 0 : 15,
  },
  // Fixed height so a fetch starting or finishing never moves the cards.
  status: {
    alignItems: 'center',
    flexDirection: 'row',
    height: 16,
    paddingHorizontal: isWeb ? 0 : 15,
  },
  statusSpinner: { marginRight: 6, width: 13 },
  statusText: { color: Colors.grey13, fontSize: 11 },
  behind: { color: Colors.red0, fontSize: CAPTION_FONT_SIZE },
  captionFigure: { color: Colors.grey13, fontSize: CAPTION_FONT_SIZE },
  cardRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 7 },
  // The four AR cards always share one row on web: a zero basis means none of them has a
  // hypothetical width to break the line on, so they divide whatever the row has. Keeping them
  // together is the requirement, so the figure is what gives — see `valueCurrency` on the card,
  // which is sized so `Rp 89.653.213.033` still fits when the row is only ~965px wide.
  // A phone keeps a real basis, so two cards cannot share 344px and they stack as before.
  wideCard: isWeb
    ? { flexBasis: 0, flexGrow: 1, minWidth: 0 }
    : { flexBasis: '100%' },
  chartCard: { flexBasis: 300, flexGrow: 1, minWidth: 0 },
});

export default SalesmanDashboardScreen;
